/**
 * Category labels for session categories
 * Shared across all components that display category names
 * Supports all 12 languages: me, en, sr, hr, bs, sl, mk, sq, bg, ro, el, tr
 */

import type { Language } from '@/types';

type CategoryTranslations = Record<Language, string>;

export const categoryLabels: Record<string, CategoryTranslations> = {
  // Legacy categories (keep for backwards compatibility)
  meditation: {
    me: 'Meditacija', en: 'Meditation', sr: 'Meditacija', hr: 'Meditacija',
    bs: 'Meditacija', sl: 'Meditacija', mk: 'Медитација', sq: 'Meditimi',
    bg: 'Медитация', ro: 'Meditație', el: 'Διαλογισμός', tr: 'Meditasyon',
  },
  health_relax: {
    me: 'Toplina i opuštanje', en: 'Heat & Relax', sr: 'Toplina i opuštanje', hr: 'Toplina i opuštanje',
    bs: 'Toplina i opuštanje', sl: 'Toplina in sprostitev', mk: 'Топлина и релаксација', sq: 'Ngrohtësi dhe relaksim',
    bg: 'Топлина и отпускане', ro: 'Căldură și relaxare', el: 'Ζεστασιά και χαλάρωση', tr: 'Sıcaklık ve rahatlama',
  },
  energy: {
    me: 'Energija', en: 'Energy', sr: 'Energija', hr: 'Energija',
    bs: 'Energija', sl: 'Energija', mk: 'Енергија', sq: 'Energji',
    bg: 'Енергия', ro: 'Energie', el: 'Ενέργεια', tr: 'Enerji',
  },
  healing: {
    me: 'Unutrašnja ravnoteža', en: 'Inner Balance', sr: 'Unutrašnja ravnoteža', hr: 'Unutarnja ravnoteža',
    bs: 'Unutrašnja ravnoteža', sl: 'Notranje ravnovesje', mk: 'Внатрешна рамнотежа', sq: 'Ekuilibër i brendshëm',
    bg: 'Вътрешно равновесие', ro: 'Echilibru interior', el: 'Εσωτερική ισορροπία', tr: 'İç denge',
  },
  chakra: {
    me: 'Čakra', en: 'Chakra', sr: 'Čakra', hr: 'Čakra',
    bs: 'Čakra', sl: 'Čakra', mk: 'Чакра', sq: 'Çakra',
    bg: 'Чакра', ro: 'Chakra', el: 'Τσάκρα', tr: 'Çakra',
  },
  sleep: {
    me: 'San', en: 'Sleep', sr: 'San', hr: 'San',
    bs: 'San', sl: 'Spanje', mk: 'Сон', sq: 'Gjumi',
    bg: 'Сън', ro: 'Somn', el: 'Ύπνος', tr: 'Uyku',
  },
  breathing: {
    me: 'Disanje', en: 'Breathing', sr: 'Disanje', hr: 'Disanje',
    bs: 'Disanje', sl: 'Dihanje', mk: 'Дишење', sq: 'Frymëmarrja',
    bg: 'Дишане', ro: 'Respirație', el: 'Αναπνοή', tr: 'Nefes',
  },
  manifestation: {
    me: 'Manifestacija', en: 'Manifestation', sr: 'Manifestacija', hr: 'Manifestacija',
    bs: 'Manifestacija', sl: 'Manifestacija', mk: 'Манифестација', sq: 'Manifestimi',
    bg: 'Манифестация', ro: 'Manifestare', el: 'Εκδήλωση', tr: 'Tezahür',
  },
  inspiration_focus: {
    me: 'Inspiracija i fokus', en: 'Inspiration & Focus', sr: 'Inspiracija i fokus', hr: 'Inspiracija i fokus',
    bs: 'Inspiracija i fokus', sl: 'Inspiracija in fokus', mk: 'Инспирација и фокус', sq: 'Frymëzim dhe fokus',
    bg: 'Вдъхновение и фокус', ro: 'Inspirație și concentrare', el: 'Έμπνευση και εστίαση', tr: 'İlham ve odaklanma',
  },
  buster: {
    me: 'Energetski buster', en: 'Energy Buster', sr: 'Energetski buster', hr: 'Energetski buster',
    bs: 'Energetski buster', sl: 'Energijski buster', mk: 'Енергетски бустер', sq: 'Buster energjie',
    bg: 'Енергиен бустер', ro: 'Impuls energetic', el: 'Ενεργειακή ώθηση', tr: 'Enerji patlaması',
  },
  energy_healing: {
    me: 'Energetska praksa', en: 'Energy Practice', sr: 'Energetska praksa', hr: 'Energetska praksa',
    bs: 'Energetska praksa', sl: 'Energijska praksa', mk: 'Енергетска пракса', sq: 'Praktikë energjetike',
    bg: 'Енергийна практика', ro: 'Practică energetică', el: 'Ενεργειακή πρακτική', tr: 'Enerji pratiği',
  },
  chakra_balance: {
    me: 'Balans čakri', en: 'Chakra Balance', sr: 'Balans čakri', hr: 'Balans čakri',
    bs: 'Balans čakri', sl: 'Ravnovesje čaker', mk: 'Баланс на чакри', sq: 'Balanca e çakrave',
    bg: 'Баланс на чакрите', ro: 'Echilibrul chakrelor', el: 'Ισορροπία τσάκρα', tr: 'Çakra dengesi',
  },
  stress_relief: {
    me: 'Rasterećenje', en: 'Unwinding', sr: 'Rasterećenje', hr: 'Rasterećenje',
    bs: 'Rasterećenje', sl: 'Razbremenitev', mk: 'Растоварување', sq: 'Çlirim',
    bg: 'Разтоварване', ro: 'Destindere', el: 'Αποφόρτιση', tr: 'Hafifleme',
  },
  focus: {
    me: 'Fokus', en: 'Focus', sr: 'Fokus', hr: 'Fokus',
    bs: 'Fokus', sl: 'Fokus', mk: 'Фокус', sq: 'Fokusi',
    bg: 'Фокус', ro: 'Concentrare', el: 'Εστίαση', tr: 'Odaklanma',
  },

  // Lukaaji session categories
  lukaaji_meditation: {
    me: 'Lukaaji Meditacija', en: 'Lukaaji Meditation', sr: 'Lukaaji Meditacija', hr: 'Lukaaji Meditacija',
    bs: 'Lukaaji Meditacija', sl: 'Lukaaji Meditacija', mk: 'Lukaaji Медитација', sq: 'Lukaaji Meditim',
    bg: 'Lukaaji Медитация', ro: 'Lukaaji Meditație', el: 'Lukaaji Διαλογισμός', tr: 'Lukaaji Meditasyon',
  },
  lukaaji_presence: {
    me: 'Prisutnost', en: 'Presence', sr: 'Prisutnost', hr: 'Prisutnost',
    bs: 'Prisutnost', sl: 'Prisotnost', mk: 'Присутност', sq: 'Prania',
    bg: 'Присъствие', ro: 'Prezență', el: 'Παρουσία', tr: 'Mevcudiyet',
  },
  lukaaji_relationships: {
    me: 'Odnosi', en: 'Relationships', sr: 'Odnosi', hr: 'Odnosi',
    bs: 'Odnosi', sl: 'Odnosi', mk: 'Односи', sq: 'Marrëdhëniet',
    bg: 'Отношения', ro: 'Relații', el: 'Σχέσεις', tr: 'İlişkiler',
  },
  lukaaji_transformation: {
    me: 'Transformacija', en: 'Transformation', sr: 'Transformacija', hr: 'Transformacija',
    bs: 'Transformacija', sl: 'Transformacija', mk: 'Трансформација', sq: 'Transformimi',
    bg: 'Трансформация', ro: 'Transformare', el: 'Μεταμόρφωση', tr: 'Dönüşüm',
  },
  lukaaji_quick: {
    me: 'Brze sesije', en: 'Quick Sessions', sr: 'Brze sesije', hr: 'Brze sesije',
    bs: 'Brze sesije', sl: 'Hitre seje', mk: 'Брзи сесии', sq: 'Seanca të shpejta',
    bg: 'Бързи сесии', ro: 'Sesiuni rapide', el: 'Γρήγορες συνεδρίες', tr: 'Hızlı seanslar',
  },
  lukaaji_sleep_trust: {
    me: 'San i povjerenje', en: 'Sleep & Trust', sr: 'San i poverenje', hr: 'San i povjerenje',
    bs: 'San i povjerenje', sl: 'Spanje in zaupanje', mk: 'Сон и доверба', sq: 'Gjumi dhe besimi',
    bg: 'Сън и доверие', ro: 'Somn și încredere', el: 'Ύπνος και εμπιστοσύνη', tr: 'Uyku ve güven',
  },
  lukaaji_purpose: {
    me: 'Svrha', en: 'Purpose', sr: 'Svrha', hr: 'Svrha',
    bs: 'Svrha', sl: 'Namen', mk: 'Цел', sq: 'Qëllimi',
    bg: 'Цел', ro: 'Scop', el: 'Σκοπός', tr: 'Amaç',
  },
  lukaaji_strength: {
    me: 'Snaga', en: 'Strength', sr: 'Snaga', hr: 'Snaga',
    bs: 'Snaga', sl: 'Moč', mk: 'Сила', sq: 'Forca',
    bg: 'Сила', ro: 'Putere', el: 'Δύναμη', tr: 'Güç',
  },
  lukaaji_emotions: {
    me: 'Emocije', en: 'Emotions', sr: 'Emocije', hr: 'Emocije',
    bs: 'Emocije', sl: 'Čustva', mk: 'Емоции', sq: 'Emocionet',
    bg: 'Емоции', ro: 'Emoții', el: 'Συναισθήματα', tr: 'Duygular',
  },
  lukaaji_creativity: {
    me: 'Kreativnost', en: 'Creativity', sr: 'Kreativnost', hr: 'Kreativnost',
    bs: 'Kreativnost', sl: 'Ustvarjalnost', mk: 'Креативност', sq: 'Kreativiteti',
    bg: 'Креативност', ro: 'Creativitate', el: 'Δημιουργικότητα', tr: 'Yaratıcılık',
  },
  lukaaji_joy: {
    me: 'Radost', en: 'Joy', sr: 'Radost', hr: 'Radost',
    bs: 'Radost', sl: 'Veselje', mk: 'Радост', sq: 'Gëzimi',
    bg: 'Радост', ro: 'Bucurie', el: 'Χαρά', tr: 'Neşe',
  },
  lukaaji_action: {
    me: 'Akcija', en: 'Action', sr: 'Akcija', hr: 'Akcija',
    bs: 'Akcija', sl: 'Akcija', mk: 'Акција', sq: 'Veprimi',
    bg: 'Действие', ro: 'Acțiune', el: 'Δράση', tr: 'Eylem',
  },
  lukaaji_expression: {
    me: 'Izražavanje', en: 'Expression', sr: 'Izražavanje', hr: 'Izražavanje',
    bs: 'Izražavanje', sl: 'Izražanje', mk: 'Изразување', sq: 'Shprehja',
    bg: 'Изразяване', ro: 'Exprimare', el: 'Έκφραση', tr: 'İfade',
  },
  lukaaji_chakra: {
    me: 'Čakre', en: 'Chakras', sr: 'Čakre', hr: 'Čakre',
    bs: 'Čakre', sl: 'Čakre', mk: 'Чакри', sq: 'Çakrat',
    bg: 'Чакри', ro: 'Chakre', el: 'Τσάκρα', tr: 'Çakralar',
  },
  lukaaji_selflove: {
    me: 'Samoljubav', en: 'Self-Love', sr: 'Samoljubav', hr: 'Samoljubav',
    bs: 'Samoljubav', sl: 'Samoljubezen', mk: 'Самољубов', sq: 'Vetëdashuria',
    bg: 'Самообич', ro: 'Iubire de sine', el: 'Αυταγάπη', tr: 'Özsevgi',
  },
  lukaaji_prosperity: {
    me: 'Prosperitet', en: 'Prosperity', sr: 'Prosperitet', hr: 'Prosperitet',
    bs: 'Prosperitet', sl: 'Blaginja', mk: 'Просперитет', sq: 'Prosperiteti',
    bg: 'Просперитет', ro: 'Prosperitate', el: 'Ευημερία', tr: 'Refah',
  },
  lukaaji_silence: {
    me: 'Tišina', en: 'Silence', sr: 'Tišina', hr: 'Tišina',
    bs: 'Tišina', sl: 'Tišina', mk: 'Тишина', sq: 'Heshtja',
    bg: 'Тишина', ro: 'Liniște', el: 'Σιωπή', tr: 'Sessizlik',
  },
  lukaaji_roots: {
    me: 'Korijeni', en: 'Roots', sr: 'Koreni', hr: 'Korijeni',
    bs: 'Korijeni', sl: 'Korenine', mk: 'Корени', sq: 'Rrënjët',
    bg: 'Корени', ro: 'Rădăcini', el: 'Ρίζες', tr: 'Kökler',
  },
  lukaaji_life_space: {
    me: 'Životni prostor', en: 'Life Space', sr: 'Životni prostor', hr: 'Životni prostor',
    bs: 'Životni prostor', sl: 'Življenjski prostor', mk: 'Животен простор', sq: 'Hapësira e jetës',
    bg: 'Жизнено пространство', ro: 'Spațiu de viață', el: 'Χώρος ζωής', tr: 'Yaşam alanı',
  },
  lukaaji_gratitude: {
    me: 'Zahvalnost', en: 'Gratitude', sr: 'Zahvalnost', hr: 'Zahvalnost',
    bs: 'Zahvalnost', sl: 'Hvaležnost', mk: 'Благодарност', sq: 'Mirënjohja',
    bg: 'Благодарност', ro: 'Recunoștință', el: 'Ευγνωμοσύνη', tr: 'Şükran',
  },
  lukaaji_addiction: {
    me: 'Navike i obrasci', en: 'Habits & Patterns', sr: 'Navike i obrasci', hr: 'Navike i obrasci',
    bs: 'Navike i obrasci', sl: 'Navade in vzorci', mk: 'Навики и обрасци', sq: 'Zakone dhe modele',
    bg: 'Навици и модели', ro: 'Obiceiuri și tipare', el: 'Συνήθειες και μοτίβα', tr: 'Alışkanlıklar ve kalıplar',
  },
  // Akustične programs (A4): shown on Omiljene, Istorija and a session's own page; the program
  // name is the session title, so the label names the section (as the Akustične nav item).
  acoustic_focus_inspiration: {
    me: 'Akustične sesije', en: 'Acoustic sessions', sr: 'Akustične sesije', hr: 'Akustične sesije',
    bs: 'Akustične sesije', sl: 'Akustične seje', mk: 'Акустични сесии', sq: 'Seancat akustike',
    bg: 'Акустични сесии', ro: 'Sesiuni acustice', el: 'Ηχητικές συνεδρίες', tr: 'Akustik seanslar',
  },
  acoustic_heat_relax: {
    me: 'Akustične sesije', en: 'Acoustic sessions', sr: 'Akustične sesije', hr: 'Akustične sesije',
    bs: 'Akustične sesije', sl: 'Akustične seje', mk: 'Акустични сесии', sq: 'Seancat akustike',
    bg: 'Акустични сесии', ro: 'Sesiuni acustice', el: 'Ηχητικές συνεδρίες', tr: 'Akustik seanslar',
  },
};

/**
 * Get translated category label
 * Falls back to Montenegrin for Balkan languages that share similar words,
 * then to English, then to the raw category key
 */
export function getCategoryLabel(
  category: string,
  language: string
): string {
  const labels = categoryLabels[category];
  if (!labels) return category;

  // Try exact language match first
  if (labels[language as Language]) {
    return labels[language as Language];
  }

  // Fallback to Montenegrin (close to other Balkan languages), then English
  return labels.me || labels.en || category;
}

/**
 * Categories for filter UI
 * Shorter labels used as filter chips/buttons
 */
export const filterCategories: { value: string; label: CategoryTranslations }[] = [
  {
    value: 'all',
    label: {
      me: 'Sve', en: 'All', sr: 'Sve', hr: 'Sve',
      bs: 'Sve', sl: 'Vse', mk: 'Сите', sq: 'Të gjitha',
      bg: 'Всички', ro: 'Toate', el: 'Όλα', tr: 'Tümü',
    },
  },
  {
    value: 'lukaaji_meditation',
    label: {
      me: 'Meditacija', en: 'Meditation', sr: 'Meditacija', hr: 'Meditacija',
      bs: 'Meditacija', sl: 'Meditacija', mk: 'Медитација', sq: 'Meditim',
      bg: 'Медитация', ro: 'Meditație', el: 'Διαλογισμός', tr: 'Meditasyon',
    },
  },
  {
    value: 'lukaaji_presence',
    label: {
      me: 'Prisutnost', en: 'Presence', sr: 'Prisutnost', hr: 'Prisutnost',
      bs: 'Prisutnost', sl: 'Prisotnost', mk: 'Присутност', sq: 'Prania',
      bg: 'Присъствие', ro: 'Prezență', el: 'Παρουσία', tr: 'Mevcudiyet',
    },
  },
  {
    value: 'lukaaji_relationships',
    label: {
      me: 'Odnosi', en: 'Relationships', sr: 'Odnosi', hr: 'Odnosi',
      bs: 'Odnosi', sl: 'Odnosi', mk: 'Односи', sq: 'Marrëdhëniet',
      bg: 'Отношения', ro: 'Relații', el: 'Σχέσεις', tr: 'İlişkiler',
    },
  },
  {
    value: 'lukaaji_transformation',
    label: {
      me: 'Transformacija', en: 'Transformation', sr: 'Transformacija', hr: 'Transformacija',
      bs: 'Transformacija', sl: 'Transformacija', mk: 'Трансформација', sq: 'Transformimi',
      bg: 'Трансформация', ro: 'Transformare', el: 'Μεταμόρφωση', tr: 'Dönüşüm',
    },
  },
  {
    value: 'lukaaji_quick',
    label: {
      me: 'Brze sesije', en: 'Quick', sr: 'Brze sesije', hr: 'Brze sesije',
      bs: 'Brze sesije', sl: 'Hitre', mk: 'Брзи', sq: 'Të shpejta',
      bg: 'Бързи', ro: 'Rapide', el: 'Γρήγορες', tr: 'Hızlı',
    },
  },
  {
    value: 'lukaaji_sleep_trust',
    label: {
      me: 'San', en: 'Sleep', sr: 'San', hr: 'San',
      bs: 'San', sl: 'Spanje', mk: 'Сон', sq: 'Gjumi',
      bg: 'Сън', ro: 'Somn', el: 'Ύπνος', tr: 'Uyku',
    },
  },
  {
    value: 'lukaaji_purpose',
    label: {
      me: 'Svrha', en: 'Purpose', sr: 'Svrha', hr: 'Svrha',
      bs: 'Svrha', sl: 'Namen', mk: 'Цел', sq: 'Qëllimi',
      bg: 'Цел', ro: 'Scop', el: 'Σκοπός', tr: 'Amaç',
    },
  },
  {
    value: 'lukaaji_strength',
    label: {
      me: 'Snaga', en: 'Strength', sr: 'Snaga', hr: 'Snaga',
      bs: 'Snaga', sl: 'Moč', mk: 'Сила', sq: 'Forca',
      bg: 'Сила', ro: 'Putere', el: 'Δύναμη', tr: 'Güç',
    },
  },
  {
    value: 'lukaaji_emotions',
    label: {
      me: 'Emocije', en: 'Emotions', sr: 'Emocije', hr: 'Emocije',
      bs: 'Emocije', sl: 'Čustva', mk: 'Емоции', sq: 'Emocionet',
      bg: 'Емоции', ro: 'Emoții', el: 'Συναισθήματα', tr: 'Duygular',
    },
  },
  {
    value: 'lukaaji_creativity',
    label: {
      me: 'Kreativnost', en: 'Creativity', sr: 'Kreativnost', hr: 'Kreativnost',
      bs: 'Kreativnost', sl: 'Ustvarjalnost', mk: 'Креативност', sq: 'Kreativiteti',
      bg: 'Креативност', ro: 'Creativitate', el: 'Δημιουργικότητα', tr: 'Yaratıcılık',
    },
  },
  {
    value: 'lukaaji_joy',
    label: {
      me: 'Radost', en: 'Joy', sr: 'Radost', hr: 'Radost',
      bs: 'Radost', sl: 'Veselje', mk: 'Радост', sq: 'Gëzimi',
      bg: 'Радост', ro: 'Bucurie', el: 'Χαρά', tr: 'Neşe',
    },
  },
  {
    value: 'lukaaji_action',
    label: {
      me: 'Akcija', en: 'Action', sr: 'Akcija', hr: 'Akcija',
      bs: 'Akcija', sl: 'Akcija', mk: 'Акција', sq: 'Veprimi',
      bg: 'Действие', ro: 'Acțiune', el: 'Δράση', tr: 'Eylem',
    },
  },
  {
    value: 'lukaaji_expression',
    label: {
      me: 'Izražavanje', en: 'Expression', sr: 'Izražavanje', hr: 'Izražavanje',
      bs: 'Izražavanje', sl: 'Izražanje', mk: 'Изразување', sq: 'Shprehja',
      bg: 'Изразяване', ro: 'Exprimare', el: 'Έκφραση', tr: 'İfade',
    },
  },
  {
    value: 'lukaaji_chakra',
    label: {
      me: 'Čakre', en: 'Chakras', sr: 'Čakre', hr: 'Čakre',
      bs: 'Čakre', sl: 'Čakre', mk: 'Чакри', sq: 'Çakrat',
      bg: 'Чакри', ro: 'Chakre', el: 'Τσάκρα', tr: 'Çakralar',
    },
  },
  {
    value: 'lukaaji_selflove',
    label: {
      me: 'Samoljubav', en: 'Self-Love', sr: 'Samoljubav', hr: 'Samoljubav',
      bs: 'Samoljubav', sl: 'Samoljubezen', mk: 'Самољубов', sq: 'Vetëdashuria',
      bg: 'Самообич', ro: 'Iubire de sine', el: 'Αυταγάπη', tr: 'Özsevgi',
    },
  },
  {
    value: 'lukaaji_prosperity',
    label: {
      me: 'Prosperitet', en: 'Prosperity', sr: 'Prosperitet', hr: 'Prosperitet',
      bs: 'Prosperitet', sl: 'Blaginja', mk: 'Просперитет', sq: 'Prosperiteti',
      bg: 'Просперитет', ro: 'Prosperitate', el: 'Ευημερία', tr: 'Refah',
    },
  },
  {
    value: 'lukaaji_silence',
    label: {
      me: 'Tišina', en: 'Silence', sr: 'Tišina', hr: 'Tišina',
      bs: 'Tišina', sl: 'Tišina', mk: 'Тишина', sq: 'Heshtja',
      bg: 'Тишина', ro: 'Liniște', el: 'Σιωπή', tr: 'Sessizlik',
    },
  },
  {
    value: 'lukaaji_roots',
    label: {
      me: 'Korijeni', en: 'Roots', sr: 'Koreni', hr: 'Korijeni',
      bs: 'Korijeni', sl: 'Korenine', mk: 'Корени', sq: 'Rrënjët',
      bg: 'Корени', ro: 'Rădăcini', el: 'Ρίζες', tr: 'Kökler',
    },
  },
  {
    value: 'lukaaji_life_space',
    label: {
      me: 'Životni prostor', en: 'Life Space', sr: 'Životni prostor', hr: 'Životni prostor',
      bs: 'Životni prostor', sl: 'Življenjski prostor', mk: 'Животен простор', sq: 'Hapësira e jetës',
      bg: 'Жизнено пространство', ro: 'Spațiu de viață', el: 'Χώρος ζωής', tr: 'Yaşam alanı',
    },
  },
  {
    value: 'lukaaji_gratitude',
    label: {
      me: 'Zahvalnost', en: 'Gratitude', sr: 'Zahvalnost', hr: 'Zahvalnost',
      bs: 'Zahvalnost', sl: 'Hvaležnost', mk: 'Благодарност', sq: 'Mirënjohja',
      bg: 'Благодарност', ro: 'Recunoștință', el: 'Ευγνωμοσύνη', tr: 'Şükran',
    },
  },
  {
    value: 'lukaaji_addiction',
    label: {
      me: 'Navike', en: 'Habits', sr: 'Navike', hr: 'Navike',
      bs: 'Navike', sl: 'Navade', mk: 'Навики', sq: 'Zakone',
      bg: 'Навици', ro: 'Obiceiuri', el: 'Συνήθειες', tr: 'Alışkanlıklar',
    },
  },
];
